import { Component, effect, ElementRef, ViewChild, WritableSignal } from '@angular/core';
import { MenuItem } from 'primeng/api';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { CommonModule } from '@angular/common';
import { Subscription } from 'src/app/shared/models/model/concretes/shared/subcription.model';
import { SharedDataService } from 'src/app/shared/services/shared-data.service';
import { filter } from 'rxjs';

@Component({
  selector: 'app-breadcrumb',
  templateUrl: './breadcrumb.component.html',
  standalone: true,
  imports: [CommonModule, BreadcrumbModule, RouterModule]
})
export class BreadcrumbComponent {
  items: MenuItem[] = [];

  @ViewChild('scrollContainer', { static: false }) scrollContainer!: ElementRef<HTMLElement>;

  subscription: WritableSignal<Subscription | null>;

  constructor(
    private sharedDataService: SharedDataService,
    private router: Router
  ) {
    this.subscription = this.sharedDataService.getSubscription();

    // Reconstruire le breadcrumb quand la subscription change
    effect(() => {
      const subSignal = this.subscription();
      if (!subSignal) {
        this.items = [];
        return;
      }
      this.updateBreadcrumb(subSignal);
      // small delay to ensure DOM a rendu
      setTimeout(() => this.scrollToRight(), 0);
    });

    // Toujours réagir aux NavigationEnd pour garder le modèle synchronisé
    this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe(event => {
        const sub = this.subscription();
        if (!sub) return;

        sub.activateStepByUrl(event.urlAfterRedirects);

        this.sharedDataService.forceUpdateSubscription?.();

        this.updateBreadcrumb(sub);

        setTimeout(() => this.scrollToRight(), 0);
      });
  }

  private updateBreadcrumb(subscription: Subscription | null) {
    if (!subscription) {
      this.items = [];
      return;
    }

    const activeGroup = subscription.groupSteps.find(g => g.steps.some(s => s.isActive));
    if (!activeGroup) {
      this.items = [];
      return;
    }

    this.items = activeGroup.steps
      .filter(step => step.layoutInfo.breadcrumb !== '' && (step.isActive || step.isCompleted))
      .map(
        step =>
          ({
            label: step.layoutInfo.breadcrumb,
            url: '/subscription/' + step.url,
            stepUrl: step.url
          }) as any
      );
  }

  onBreadcrumbClick(item: any, event?: Event) {
    if (event) event.preventDefault();

    const sub = this.subscription();
    if (!sub) return;

    const stepUrl = item.stepUrl ?? (item.url ? item.url.replace('/subscription/', '') : undefined);
    if (!stepUrl) {
      if (item.url) this.router.navigateByUrl(item.url);
      return;
    }

    let foundGroupIndex: number | undefined;
    let foundStepIndex: number | undefined;

    sub.groupSteps.forEach((group, gIdx) => {
      group.steps.forEach((step, sIdx) => {
        if (step.url === stepUrl) {
          foundGroupIndex = gIdx;
          foundStepIndex = sIdx;
        }
      });
    });

    if (foundGroupIndex != null && foundStepIndex != null) {
      // Mettre à jour le modèle AVANT la navigation
      sub.activateStepByUrl(stepUrl, foundGroupIndex);
      this.sharedDataService.setGroupStep?.(foundGroupIndex);
      this.sharedDataService.setStepIndex?.(foundStepIndex);
      this.sharedDataService.forceUpdateSubscription?.();
    }

    if (item.url) {
      this.router.navigateByUrl(item.url);
    }
  }

  private scrollToRight() {
    const el = this.scrollContainer?.nativeElement ?? document.getElementById('scroll-container');
    if (el) {
      try {
        el.scrollTo({ left: el.scrollWidth, behavior: 'smooth' });
      } catch {
        el.scrollLeft = el.scrollWidth;
      }
    }
  }
}
